import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import KineticPresetPreview from '../components/clip-editor/KineticPresetPreview';
import BrandLogo from '../components/BrandLogo';
import { setPendingSelection } from '../utils/choosePlan';
import './landing-page.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Pricing catalog types (mirrors backend UserPricingCatalogResponseDto) ──
interface PaygItem {
  featureKey: string;
  label: string;
  unit: string;
  pricePerUnit: number;
  currency: 'INR' | 'USD';
  price: number;
  maxPerMonth: number | null;
}
interface PlanLimit {
  featureKey: string;
  label: string;
  unit: string;
  maxPerMonth: number | null;
}
interface Plan {
  id: string;
  name: string;
  priceMonthly: number;
  currency: 'INR' | 'USD';
  price: number;
  sortOrder: number;
  limits: PlanLimit[];
}
interface PricingCatalog {
  payg: PaygItem[];
  plans: Plan[];
}

// ── Constants ──
// Each persona swaps the eyebrow, headline and subline together. h1 is split
// into its two lines by hand so every persona keeps the same two-line mass and
// the CTAs below it never shift.
const USE_CASES = [
  { key: 'agency',  label: 'Agency',
    eyebrow: 'Multi-client video production',
    h1: ['Ten clients.', 'One workflow.'],
    sub: 'A project per client, consistent caption styling, and multi-account publishing — without hiring more editors.' },
  { key: 'creator', label: 'Creator',
    eyebrow: 'AI shorts · captions · publishing',
    h1: ['One video.', 'Five shorts.'],
    sub: 'Upload once. Get five titled, captioned shorts back — and publish them without leaving the app.' },
  { key: 'b2b',     label: 'B2B',
    eyebrow: 'Governed video pipelines',
    h1: ['One webinar.', 'Ninety posts.'],
    sub: 'Branded pipelines, a confidence score on every clip, and governed publishing across the channels you own.' },
] as const;

// Personas hold for three image beats, so only one half of the hero is ever
// changing at a time.
const USE_CASE_MS = 15000;

type Feature = { icon: string; title: string; desc: string; previewUrl?: string; livePresetId?: string };

const FEATURES: Feature[] = [
  { icon: '✂️', title: 'AI Shorts from Long Video',    desc: 'Upload once. The AI watches the full video, scores every moment, and returns titled clips ready to post.' },
  { icon: '📝', title: 'Kinetic Captions',             desc: 'Nine animated presets — karaoke, typewriter, word pop — timed to the word against a transcript you can edit.', previewUrl: '/previews/caption-reel.html', livePresetId: 'karaoke-box' },
  { icon: '🎞️', title: 'Full Clip Editor',             desc: 'Trim, split, merge and reorder segments, then layer crops, transitions, filters and music over the result.' },
  { icon: '📚', title: 'Story Builder',                desc: 'Compose slide-by-slide videos from images and clips, with motion, narration and a background mix per slide.' },
  { icon: '🌐', title: '99 Languages, Auto-Detected',  desc: 'Five transcription models pick up what is spoken on their own — including 16 Indian languages and Hinglish.' },
  { icon: '🔗', title: 'Multi-Account Publishing',     desc: 'Connect several channels per platform over OAuth and publish from the project list — no downloads, no re-uploads.' },
  { icon: '🎯', title: 'Confidence Score per Clip',    desc: 'Every clip ships with a 0–100 score for how strongly the AI rated that moment, so you know which to publish first.' },
  { icon: '🗂️', title: 'One Project Workspace',        desc: 'Stories, captions and shorts in one list, with live render status and publish state on every row.' },
];

const STEPS = [
  { label: 'Step 01', title: 'Upload',            desc: 'Drop a long video, pick a language and transcription model, then hit Proceed.', ytMini: false, shot: '/demo/quick-shorts-modal.png', shotAlt: 'Create Shorts quick action modal with name, language and model fields' },
  { label: 'Step 02', title: 'Generate',          desc: 'AI transcribes, finds the strongest moments, and renders titled clips with captions.', ytMini: false, shot: '/demo/project-workspace.png', shotAlt: 'Project workspace listing stories, captions and shorts with render status' },
  { label: 'Step 03', title: 'Refine',            desc: 'Open the clip editor or caption studio to trim segments and restyle subtitles.', ytMini: false, shot: '/demo/clip-editor.png', shotAlt: 'Clip editor timeline with four trimmed segments' },
  { label: 'Step 04', title: 'Connect & Publish', desc: 'Link your channels once, then publish straight from the project list.', ytMini: true, shot: '/demo/social-accounts.png', shotAlt: 'Social accounts page with connected YouTube channel' },
];

const PREVIEW_TABS = [
  { key: 'shorts',   label: 'AI Shorts',      outcome: 'Five publish-ready clips from one upload.',  caption: 'Each render is titled, scored and timestamped back to the source video.', shot: '/demo/shorts-workspace-light.png', alt: 'Shorts detail view with renders panel and clip metadata', z: 1.28, zx: '62%', zy: '12%' },
  { key: 'captions', label: 'Kinetic Captions', outcome: 'Captions people actually read.',            caption: 'Nine animated presets, four aspect ratios, and a fully editable transcript.', shot: '/demo/kinetic-captions.png', alt: 'Caption studio showing kinetic preset picker and editable transcript', z: 1.26, zx: '98%', zy: '21%' },
  { key: 'clip',     label: 'Clip Editor',    outcome: 'Cut it exactly how you want it.',            caption: 'Trim, split, merge and duplicate segments, then layer captions, emojis and music.', shot: '/demo/clip-editor.png', alt: 'Clip editor with segment timeline and editing toolbar', z: 1.24, zx: '60%', zy: '76%' },
  { key: 'story',    label: 'Story Builder',  outcome: 'Slide-by-slide stories with sound.',         caption: 'Compose slides, set motion, and mix foreground narration over background audio.', shot: '/demo/story-audio.png', alt: 'Story builder audio tab with foreground and background audio controls', z: 1.26, zx: '92%', zy: '31%' },
] as const;

// ── Hero cinema ──
// One beat per product screenshot: the whole product told as a 10-beat, 50s loop
// inside the hero mockup card. Shots come from the 950px /demo/hero set, sized
// for this card; the originals in /demo stay full-size for the ZoomShot crops.
// Beats cross-fade and hold still — see the .lp-kb note in landing-page.css.
type HeroBeat = {
  shot: string;
  alt: string;
  kicker: string;
  title: string;
  body: string;
  chips: { cls: 'teal' | 'amber' | 'blue'; label: string }[];
  h: number;
};

const HERO_BEATS: HeroBeat[] = [
  { shot: '/demo/hero/quick-shorts-modal.png', h: 444,
    alt: 'Create Shorts quick action modal with name, language and model fields',
    kicker: 'Upload', title: 'Start with one long video',
    body: 'Name it, pick a language and a transcription model, hit Proceed. That is the entire setup.',
    chips: [{ cls: 'teal', label: '✓ Upload accepted' }, { cls: 'blue', label: '🌐 99 languages' }] },
  { shot: '/demo/hero/project-workspace.png', h: 449,
    alt: 'Project workspace listing stories, captions and shorts with render status',
    kicker: 'Generate', title: 'The AI watches it for you',
    body: 'It transcribes the video, finds the strongest moments, and queues them as renders — stories, captions and shorts in one project.',
    chips: [{ cls: 'amber', label: '⏳ Transcribing…' }, { cls: 'teal', label: '✓ Moments found' }] },
  { shot: '/demo/hero/shorts-workspace-dark.png', h: 452,
    alt: 'Shorts detail view in dark theme with five finished renders listed',
    kicker: 'Shorts', title: 'Five clips come back, already titled',
    body: 'Happiness Over Money. Tears of Joy. Each render is named from what was actually said in your video.',
    chips: [{ cls: 'teal', label: '✓ 5 shorts rendered' }, { cls: 'blue', label: '⏱ 0:16 each' }] },
  { shot: '/demo/hero/shorts-confidence-score.png', h: 446,
    alt: 'Clip details showing confidence score, title, description and tags',
    kicker: 'Score', title: 'Every clip is scored before you post',
    body: 'A confidence score, title, description and tags — so you know which one to publish first.',
    chips: [{ cls: 'amber', label: '🎯 Confidence 95/100' }, { cls: 'teal', label: '✓ Titles & tags written' }] },
  { shot: '/demo/hero/clip-editor.png', h: 441,
    alt: 'Clip editor timeline with four trimmed segments',
    kicker: 'Clip editor', title: 'Then you cut it your way',
    body: 'Trim, split, merge and duplicate segments on a real timeline. Generated is the starting point, not the ceiling.',
    chips: [{ cls: 'teal', label: '✓ 4 segments' }, { cls: 'blue', label: '✂ Split & merge' }] },
  { shot: '/demo/hero/kinetic-captions.png', h: 450,
    alt: 'Caption studio showing kinetic preset picker and editable transcript',
    kicker: 'Captions', title: 'Captions people actually read',
    body: 'Nine kinetic presets, four aspect ratios, and a transcript you can edit line by line.',
    chips: [{ cls: 'teal', label: '✓ Captions generated' }, { cls: 'blue', label: '▦ 9 presets' }] },
  { shot: '/demo/hero/story-content.png', h: 444,
    alt: 'Story builder content tab with slide caption and narration fields',
    kicker: 'Story builder', title: 'Build stories slide by slide',
    body: 'Compose slides, set the caption and narration on each clip, then generate the whole story.',
    chips: [{ cls: 'teal', label: '✓ 2 slides · 1 clip' }, { cls: 'blue', label: '▶ 9:16 preview' }] },
  { shot: '/demo/hero/story-audio.png', h: 447,
    alt: 'Story builder audio tab with foreground and background audio controls',
    kicker: 'Audio', title: 'And give them a voice',
    body: 'Mix foreground narration over a background track without leaving the builder.',
    chips: [{ cls: 'amber', label: '🎵 Background mixed' }, { cls: 'teal', label: '✓ Narration added' }] },
  { shot: '/demo/hero/shorts-workspace-light.png', h: 450,
    alt: 'The same shorts workspace in light theme',
    kicker: 'Your workspace', title: 'Light or dark, it follows you',
    body: 'The same project, the same renders, the same controls — in whichever theme you actually work in.',
    chips: [{ cls: 'blue', label: '◑ Theme synced' }, { cls: 'teal', label: '✓ Same workspace' }] },
  { shot: '/demo/hero/social-accounts.png', h: 446,
    alt: 'Social accounts page with a connected YouTube channel',
    kicker: 'Publish', title: 'Connect once. Publish from the list.',
    body: 'Link your channel a single time, then publish straight out of the project — no downloads, no re-uploads.',
    chips: [{ cls: 'blue', label: '🔗 YouTube connected' }, { cls: 'teal', label: '✓ Published' }] },
];

const BEAT_MS = 5000;


// Mirrors PRESETS in KineticCaption.tsx — kept in sync by hand.
const CAPTION_PRESETS = [
  { id: 'word-pop',         label: 'Word Pop',         premium: false },
  { id: 'karaoke-box',      label: 'Karaoke Box',      premium: false },
  { id: 'karaoke-color',    label: 'Karaoke Color',    premium: false },
  { id: 'karaoke-scale',    label: 'Karaoke Scale',    premium: true  },
  { id: 'fill-sweep',       label: 'Fill Sweep',       premium: true  },
  { id: 'bounce-in',        label: 'Bounce In',        premium: true  },
  { id: 'typewriter',       label: 'Typewriter',       premium: true  },
  { id: 'underline-marker', label: 'Underline Marker', premium: false },
  { id: 'build-up',         label: 'Build Up',         premium: false },
];

const ASPECTS = ['9:16', '16:9', '1:1', '4:5'];

// The transcription stage special-cases these 16 Indian language codes
// (INDIAN_LANGUAGES in media-pipeline/src/stages/transcribe.ts).
const INDIAN_LANGUAGES = [
  'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi',
  'Gujarati', 'Punjabi', 'Urdu', 'Odia', 'Assamese', 'Sanskrit', 'Nepali',
  'Sindhi', 'Sinhala',
];

const OTHER_LANGUAGES = [
  'English', 'Hinglish', 'Spanish', 'French', 'German', 'Portuguese',
  'Arabic', 'Japanese', 'Korean', 'Mandarin', 'Russian', 'Italian',
];

// base.en is English-only; the other four models are multilingual.
const LANG_STATS = [
  { num: '99',  label: 'languages auto-detected', sub: 'on any multilingual model' },
  { num: '16',  label: 'Indian languages',         sub: 'explicitly recognised in the pipeline' },
  { num: '4',   label: 'multilingual models',      sub: 'base, small, medium, large-v3' },
];

const ARC_LENGTH = 377; // 270° of circle r=80, circumference≈503, 503*0.75≈377

// Mirrors the clip detail shown in the Shorts workspace.
const SAMPLE_CLIP = {
  score: 95,
  title: 'Joy Unleashed',
  description: 'Witness the joy of a family receiving a phone, changing their lives forever!',
  tags: ['#Joy', '#LifeChanging', '#Family'],
  duration: '0:25',
  sourceRange: '3:56 – 4:22',
};

const SCORE_POINTS = [
  { title: 'Scored before you post', desc: 'Every clip carries a 0–100 confidence score, so the strongest cut is obvious at a glance.' },
  { title: 'Titles and tags written for you', desc: 'Each render arrives with a headline, description and hashtags you can publish or edit.' },
  { title: 'Traceable to the source', desc: 'Every clip records the exact timestamp range it came from in your original upload.' },
];

const FAQS = [
  { q: 'How fast can I create my first short?',        a: 'Upload a video through Quick Shorts, pick a language and model, and press Proceed. AI handles transcription, moment selection, titling and caption rendering — most first shorts are ready in minutes.' },
  { q: 'What languages are supported?',                a: 'Up to 99. Four of the five transcription models — base, small, medium and large-v3 — are multilingual and detect the spoken language automatically; the fifth, base.en, is English-only and optimised for speed. On top of that, 16 Indian languages are recognised by name in the pipeline: Hindi, Tamil, Telugu, Kannada, Malayalam, Bengali, Marathi, Gujarati, Punjabi, Urdu, Odia, Assamese, Sanskrit, Nepali, Sindhi and Sinhala. Hinglish code-switching is handled as a first-class option, and you can always pin the language per job.' },
  { q: 'Can I edit what the AI produces?',             a: 'Yes. Every render opens in the clip editor, where you can trim, split, merge, duplicate and reorder segments, adjust speed and volume, and add crops, transitions, filters and music.' },
  { q: 'What caption styles are available?',           a: 'Nine kinetic presets — Word Pop, Karaoke Box, Karaoke Color, Karaoke Scale, Fill Sweep, Bounce In, Typewriter, Underline Marker and Build Up — each rendered against a transcript you can edit line by line.' },
  { q: 'What is a Story?',                             a: 'A Story is a slide-by-slide video you compose yourself. Add images or clips as slides, set motion and narration per slide, layer foreground and background audio, then generate the finished video.' },
  { q: 'What does the Confidence Score mean?',         a: 'It is a 0–100 rating the AI assigns to each generated clip, reflecting how strong that moment looked when it was selected. Use it to decide which clip to publish first.' },
  { q: 'Can I connect more than one account?',         a: 'Yes. Each platform supports multiple channels, so agencies and multi-brand creators can manage every account they run from one workspace via OAuth.' },
  { q: 'Which platforms can I publish to?',            a: 'YouTube Shorts publishing is live today. Instagram Reels, TikTok, Facebook Reels, X and LinkedIn are wired into the accounts screen and rolling out next.' },
];

// ── SEO ──
const SITE_URL = 'https://www.vimapi.com';
const OG_IMAGE = `${SITE_URL}/og-image.png`;

// Both schemas are derived from the constants above, so the structured data
// cannot drift from what the page actually renders.
const SOFTWARE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'ViralAI',
  applicationCategory: 'MultimediaApplication',
  operatingSystem: 'Web',
  url: `${SITE_URL}/`,
  image: OG_IMAGE,
  description:
    'Turn long videos into scored, titled AI shorts, style them with nine kinetic caption presets, build slide-by-slide stories, and publish to multiple channels from one workspace.',
  featureList: FEATURES.map(f => f.title),
};

const FAQ_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

// ── Hook: IntersectionObserver reveal ──
function useReveal(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

// ── Zoom shot ──
// Screenshots are full 1900px dashboard captures; at page width the detail is
// unreadable. ZoomShot crops to a focal region via transform-origin + scale, so
// each section shows the part of the UI it is actually talking about.
type Shot = { src: string; alt: string; zoom: number; x: string; y: string; ratio: number };

function ZoomShot({ shot, ratio, label, className = '' }: {
  shot: Shot; ratio?: number; label?: string; className?: string;
}) {
  return (
    <figure
      className={`lp-zoom ${className}`}
      style={{ '--z': shot.zoom, '--zx': shot.x, '--zy': shot.y, '--ratio': ratio ?? shot.ratio } as React.CSSProperties}
    >
      <img src={shot.src} alt={shot.alt} loading="lazy" />
      {label && <figcaption className="lp-zoom-label">{label}</figcaption>}
    </figure>
  );
}

// Focal points solved against the source captures. `ratio` is the crop's aspect,
// `zoom` the scale that fills it, x/y the crop's position. Each is tuned so the
// cropped UI renders at roughly 1:1 or larger in its container — a wide crop in a
// narrow column would shrink the text below the original and defeat the point.
const LENS = {
  presetGrid:  { src: '/demo/kinetic-captions.png',        alt: 'All nine kinetic caption presets in the style picker',     zoom: 1.73, x: '99%', y: '47%',  ratio: 1.0 },
  transcript:  { src: '/demo/kinetic-captions.png',        alt: 'Editable transcript with per-line start and end times',    zoom: 3.80, x: '20%', y: '100%', ratio: 2.86 },
  clipDetails: { src: '/demo/shorts-confidence-score.png', alt: 'Clip details: confidence score, title, description, tags', zoom: 1.52, x: '43%', y: '92%',  ratio: 6.94 },
} as const;

// ── Nav ──
function LandingNav({ menuOpen, setMenuOpen }: { menuOpen: boolean; setMenuOpen: (v: boolean) => void }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY >= 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <Link to="/" className="lp-logo" aria-label="VimAPI — A ViralAI Platform">
            <BrandLogo height={44} />
          </Link>
          <div className="lp-nav-links">
            <a href="#features" className="lp-nav-link">Features</a>
            <a href="#how-it-works" className="lp-nav-link">How it works</a>
            <a href="#pricing" className="lp-nav-link">Pricing</a>
          </div>
          <div className="lp-nav-right">
            <Link to="/login" className="lp-nav-login">Login</Link>
            <Link to="/login" className="lp-nav-cta">Start Free →</Link>
          </div>
          <button className="lp-nav-hamburger" onClick={() => setMenuOpen(true)} aria-label="Open menu">
            <span /><span /><span />
          </button>
        </div>
      </nav>
      <div className={`lp-nav-overlay${menuOpen ? ' open' : ''}`}>
        <button className="lp-nav-overlay-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">✕</button>
        <a href="#features"     className="lp-nav-overlay-link" onClick={() => setMenuOpen(false)}>Features</a>
        <a href="#how-it-works" className="lp-nav-overlay-link" onClick={() => setMenuOpen(false)}>How it works</a>
        <a href="#pricing"      className="lp-nav-overlay-link" onClick={() => setMenuOpen(false)}>Pricing</a>
        <Link to="/login"       className="lp-nav-overlay-link" onClick={() => setMenuOpen(false)}>Login</Link>
        <Link to="/login" className="lp-btn-primary" style={{ marginTop: 8, width: '80%', justifyContent: 'center' }}>Start Free →</Link>
      </div>
    </>
  );
}

// ── Hero ──
function LandingHero() {
  const [activeUseCase, setActiveUseCase] = useState(0);
  const [chipsVisible, setChipsVisible] = useState(false);
  const [cur, setCur] = useState(0);
  // How many beat images are mounted. Starts at 1 so the hero still loads one shot.
  const [staged, setStaged] = useState(1);
  const [hover, setHover] = useState(false);
  const [tabHidden, setTabHidden] = useState(false);
  const held = hover || tabHidden;

  const goTo = useCallback((i: number) => setCur(i), []);

  useEffect(() => {
    const t = setTimeout(() => setChipsVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Advance the persona. Clicking a pill re-runs this, so a manual pick gets a
  // full dwell before it moves on.
  useEffect(() => {
    const t = setTimeout(
      () => setActiveUseCase(i => (i + 1) % USE_CASES.length),
      USE_CASE_MS
    );
    return () => clearTimeout(t);
  }, [activeUseCase]);

  // Advance the story, unless the reader is on the card or the tab is in the background.
  useEffect(() => {
    if (held) return;
    const t = setTimeout(() => goTo((cur + 1) % HERO_BEATS.length), BEAT_MS);
    return () => clearTimeout(t);
  }, [cur, held, goTo]);

  useEffect(() => {
    const onVis = () => setTabHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Keep the next beat mounted, then trickle the rest in so a click on a far
  // dot is not blank — without paying for ten screenshots at first paint.
  useEffect(() => {
    setStaged(s => Math.max(s, Math.min(HERO_BEATS.length, cur + 2)));
  }, [cur]);

  useEffect(() => {
    if (staged >= HERO_BEATS.length) return;
    const t = setTimeout(() => setStaged(s => Math.min(HERO_BEATS.length, s + 1)), 1200);
    return () => clearTimeout(t);
  }, [staged]);

  const uc = USE_CASES[activeUseCase];
  const beat = HERO_BEATS[cur];

  return (
    <section className="lp-hero lp-section-ink">
      <div className="lp-hero-bg">
        <div className="lp-hero-glow" />
        <div className="lp-hero-dots" />
      </div>
      <div className="lp-hero-inner">
        {/* Left */}
        <div className="lp-hero-left">
          <span className="lp-hero-eyebrow" key={`e${activeUseCase}`}>{uc.eyebrow}</span>
          <h1 className="lp-h1" key={`h${activeUseCase}`}>{uc.h1[0]}<br />{uc.h1[1]}</h1>
          <p className="lp-hero-sub" key={`s${activeUseCase}`}>{uc.sub}</p>
          <div className="lp-hero-ctas">
            <Link to="/login" className="lp-btn-primary">Start Creating Free →</Link>
            <a href="#how-it-works" className="lp-btn-ghost">▶ Watch Demo</a>
          </div>
          <p className="lp-hero-micronote">No credit card required · First short in under 15 minutes</p>
          <div className="lp-use-case-pills">
            {USE_CASES.map((uc, i) => (
              <button
                key={uc.key}
                className={`lp-use-pill${activeUseCase === i ? ' active' : ''}`}
                onClick={() => setActiveUseCase(i)}
              >
                {activeUseCase === i ? '● ' : ''}{uc.label}
              </button>
            ))}
          </div>
          <div className="lp-trust-row">
            <span className="lp-trust-item"><span className="lp-trust-check">✓</span> No team needed</span>
            <span className="lp-trust-item"><span className="lp-trust-check">✓</span> Editable, not just generated</span>
            <span className="lp-trust-item"><span className="lp-trust-check">✓</span> Multi-account publishing</span>
          </div>
        </div>

        {/* Right — Mockup cinema */}
        <div className="lp-hero-right">
          <div
            className="lp-mockup-card"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocusCapture={() => setHover(true)}
            onBlurCapture={() => setHover(false)}
          >
            <div className="lp-mockup-shot">
              {HERO_BEATS.slice(0, staged).map((b, i) => (
                <div
                  key={b.shot}
                  className={`lp-kb${i === cur ? ' on' : ''}`}
                  aria-hidden={i !== cur}
                >
                  <img
                    src={b.shot}
                    alt={b.alt}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    width={950}
                    height={b.h}
                    decoding="async"
                  />
                </div>
              ))}
            </div>

            {chipsVisible && beat.chips.map((chip, i) => (
              <span
                key={i}
                className={`lp-chip ${chip.cls} lp-chip-${i + 1} visible`}
                style={{ animationDelay: `${i * 140}ms` }}
              >
                <span className="lp-chip-label" key={`${cur}-${i}`}>{chip.label}</span>
              </span>
            ))}

            <div className="lp-beat-cap">
              <span className="lp-beat-n">{('0' + (cur + 1)).slice(-2)}</span>
              <div className="lp-beat-text" key={cur}>
                <p className="lp-beat-title">{beat.title}</p>
                <p className="lp-beat-body">{beat.body}</p>
              </div>
            </div>

            <div className="lp-beat-dots">
              {HERO_BEATS.map((b, i) => (
                <button
                  key={b.shot}
                  type="button"
                  className={`lp-beat-dot${i === cur ? ' active' : ''}`}
                  onClick={() => goTo(i)}
                  aria-label={`${i + 1}. ${b.kicker}`}
                  aria-current={i === cur}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
// ── Features ──
function LandingFeatures() {
  const { ref, visible } = useReveal();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  return (
    <section className="lp-section-surf lp-section-pad" id="features">
      <div className="lp-section-inner">
        <div ref={ref} className="lp-features-header">
          <span className="lp-eyebrow">The complete toolkit</span>
          <h2 className="lp-section-heading" style={{ maxWidth: 680, margin: '0 auto 0' }}>
            Everything you need to turn long videos into shorts
          </h2>
        </div>
        <div className="lp-feature-grid">
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} feature={f} delay={i * 60} onPreview={setPreviewUrl} />
          ))}
        </div>
      </div>
      <FeaturePreviewModal url={previewUrl} onClose={() => setPreviewUrl(null)} />
    </section>
  );
}

function FeatureCard({ feature, delay, onPreview }: { feature: Feature; delay: number; onPreview: (url: string) => void }) {
  const { ref, visible } = useReveal(0.10);
  return (
    <div
      ref={ref}
      className={`lp-feature-card${visible ? ' visible' : ''}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms', position: 'relative' }}
    >
      {feature.livePresetId ? (
        <div className="lp-feature-live-thumb">
          <KineticPresetPreview presetId={feature.livePresetId} />
        </div>
      ) : (
        <div className="lp-feature-icon">{feature.icon}</div>
      )}
      <p className="lp-feature-title">{feature.title}</p>
      <p className="lp-feature-desc">{feature.desc}</p>
      {feature.previewUrl && (
        <button
          type="button"
          className="lp-feature-preview-btn"
          aria-label={`Preview ${feature.title}`}
          title="Preview"
          onClick={() => onPreview(feature.previewUrl!)}
        >
          →
        </button>
      )}
    </div>
  );
}

function FeaturePreviewModal({ url, onClose }: { url: string | null; onClose: () => void }) {
  useEffect(() => {
    if (!url) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [url, onClose]);

  if (!url) return null;

  return (
    <div className="lp-preview-overlay" onClick={onClose}>
      <div className="lp-preview-panel" onClick={e => e.stopPropagation()}>
        <div className="lp-preview-panel-head">
          <span>Live preview</span>
          <div className="lp-preview-panel-actions">
            <a className="lp-preview-open-link" href={url} target="_blank" rel="noopener noreferrer">Open in new tab ↗</a>
            <button type="button" className="lp-preview-close" aria-label="Close preview" onClick={onClose}>✕</button>
          </div>
        </div>
        <iframe
          className="lp-preview-frame"
          src={url}
          title="Feature preview"
          loading="lazy"
        />
      </div>
    </div>
  );
}

// ── How It Works ──
function LandingHowItWorks() {
  const { ref, visible } = useReveal();
  return (
    <section className="lp-section-ink lp-section-pad" id="how-it-works">
      <div className="lp-section-inner">
        <div ref={ref} className="lp-how-header">
          <span className="lp-eyebrow">The workflow</span>
          <h2 className="lp-section-heading">From upload to viral in 4 steps</h2>
        </div>
        <div className="lp-step-rail">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.label}>
              <StepCard step={step} index={i} delay={i * 100} />
              {i < STEPS.length - 1 && <div className="lp-step-arrow">→</div>}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

function StepCard({ step, index, delay }: { step: typeof STEPS[number]; index: number; delay: number }) {
  const { ref, visible } = useReveal(0.10);
  return (
    <div
      ref={ref}
      className={`lp-step-card${visible ? ' visible' : ''}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
    >
      <p className="lp-step-label">{step.label}</p>
      <p className="lp-step-title">{step.title}</p>
      <p className="lp-step-desc">{step.desc}</p>
      {step.shot && (
        <div className="lp-step-shot">
          <img src={step.shot} alt={step.shotAlt} loading="lazy" />
        </div>
      )}
      {step.ytMini && (
        <div className="lp-yt-mini">
          <p className="lp-yt-mini-title">Connect a channel in 2 minutes</p>
          <div className="lp-yt-steps">
            <span className="lp-yt-step">Connect</span>
            <span className="lp-yt-step-arrow">→</span>
            <span className="lp-yt-step">OAuth</span>
            <span className="lp-yt-step-arrow">→</span>
            <span className="lp-yt-step">Verified ✓</span>
          </div>
          <p className="lp-yt-trust">Secure OAuth · No password stored · Multiple channels per platform</p>
        </div>
      )}
    </div>
  );
}

// ── Product Preview ──
function LandingProductPreview() {
  const [active, setActive] = useState<string>('shorts');
  const [fading, setFading] = useState(false);

  const switchTab = useCallback((key: string) => {
    if (key === active) return;
    setFading(true);
    setTimeout(() => { setActive(key); setFading(false); }, 90);
  }, [active]);

  const tab = PREVIEW_TABS.find(t => t.key === active) ?? PREVIEW_TABS[0];

  return (
    <section className="lp-section-surf lp-section-pad">
      <div className="lp-section-inner">
        <div className="lp-preview-header">
          <span className="lp-eyebrow">See it in action</span>
          <h2 className="lp-section-heading">Four editors, one workspace</h2>
          <div className="lp-pill-tabs">
            {PREVIEW_TABS.map(t => (
              <button key={t.key} className={`lp-pill-tab${active === t.key ? ' active' : ''}`} onClick={() => switchTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="lp-shot-frame" style={{ opacity: fading ? 0 : 1 }}>
          <div className="lp-shot-chrome">
            <span className="lp-shot-dot" /><span className="lp-shot-dot" /><span className="lp-shot-dot" />
            <span className="lp-shot-chrome-label">{tab.label}</span>
            <span className="lp-shot-hint">hover to zoom</span>
          </div>
          {PREVIEW_TABS.map(t => (
            <div
              key={t.key}
              className="lp-shot-zoom"
              hidden={t.key !== active}
              style={{ '--z': t.z, '--zx': t.zx, '--zy': t.zy } as React.CSSProperties}
            >
              <img className="lp-shot-img" src={t.shot} alt={t.alt} loading="lazy" />
            </div>
          ))}
        </div>

        <div className="lp-preview-caption">
          <p className="lp-preview-outcome">{tab.outcome}</p>
          <p className="lp-preview-text">{tab.caption}</p>
        </div>
      </div>
    </section>
  );
}

// ── Caption Styles ──
function LandingCaptionStyles() {
  const { ref } = useReveal(0.15);
  const [active, setActive] = useState('karaoke-box');

  return (
    <section className="lp-section-ink lp-section-pad" id="captions">
      <div className="lp-section-inner">
        <div ref={ref} className="lp-caption-header">
          <span className="lp-eyebrow">Caption studio</span>
          <h2 className="lp-section-heading">Nine caption styles, animated to the word</h2>
          <p className="lp-caption-sub">
            Captions are timed against the transcript, so every word lands on beat.
            Pick a preset, pick an aspect ratio, and render.
          </p>
        </div>

        <div className="lp-caption-grid">
          {/* Live preset picker */}
          <div className="lp-caption-live">
            <div className="lp-caption-stage">
              <KineticPresetPreview key={active} presetId={active} />
            </div>
            <div className="lp-preset-chips">
              {CAPTION_PRESETS.map(p => (
                <button
                  key={p.id}
                  className={`lp-preset-chip${active === p.id ? ' active' : ''}`}
                  onClick={() => setActive(p.id)}
                  aria-pressed={active === p.id}
                >
                  {p.label}
                  {p.premium && <span className="lp-preset-pro" title="Premium preset">PRO</span>}
                </button>
              ))}
            </div>
            <div className="lp-aspect-row">
              <span className="lp-aspect-label">Aspect ratio</span>
              {ASPECTS.map(a => (
                <span key={a} className={`lp-aspect-pill${a === '9:16' ? ' active' : ''}`}>{a}</span>
              ))}
            </div>
          </div>

          {/* Zoomed detail of the real studio */}
          <div className="lp-caption-shots">
            <ZoomShot shot={LENS.presetGrid} label="All nine presets, live in the app" />
            <ZoomShot shot={LENS.transcript} label="Every line editable, with timings" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Languages ──
function LandingLanguages() {
  const { ref, visible } = useReveal(0.15);
  return (
    <section className="lp-section-surf lp-section-pad" id="languages">
      <div className="lp-section-inner">
        <div ref={ref} className="lp-lang-head">
          <span className="lp-eyebrow">Speak to everyone</span>
          <h2 className="lp-section-heading">99 languages in. Captions out.</h2>
          <p className="lp-lang-sub">
            Leave it on auto-detect and the model works out what is being spoken.
            Sixteen Indian languages are recognised by name, and Hinglish
            code-switching is handled as a first-class option.
          </p>
        </div>

        <div className="lp-lang-stats">
          {LANG_STATS.map(st => (
            <div key={st.label} className="lp-lang-stat">
              <span className="lp-lang-stat-num">{st.num}</span>
              <span className="lp-lang-stat-label">{st.label}</span>
              <span className="lp-lang-stat-sub">{st.sub}</span>
            </div>
          ))}
        </div>

        <div className={`lp-lang-cloud${visible ? ' visible' : ''}`}>
          {INDIAN_LANGUAGES.map((l, i) => (
            <span key={l} className="lp-lang-pill indian" style={{ transitionDelay: `${i * 20}ms` }}>{l}</span>
          ))}
          {OTHER_LANGUAGES.map((l, i) => (
            <span key={l} className="lp-lang-pill" style={{ transitionDelay: `${(INDIAN_LANGUAGES.length + i) * 20}ms` }}>{l}</span>
          ))}
          <span className="lp-lang-pill more" style={{ transitionDelay: '580ms' }}>+ 71 more</span>
        </div>

        <div className="lp-lang-legend">
          <span className="lp-lang-legend-item"><i className="lp-swatch indian" /> Recognised by name in the pipeline</span>
          <span className="lp-lang-legend-item"><i className="lp-swatch" /> Auto-detected</span>
          <span className="lp-lang-legend-note">base.en is English-only and tuned for speed — pick any other model for multilingual work.</span>
        </div>
      </div>
    </section>
  );
}

// ── Confidence Score ──
function LandingConfidenceScore() {
  const { ref, visible } = useReveal(0.20);
  const arcOffset = ARC_LENGTH - (ARC_LENGTH * SAMPLE_CLIP.score / 100);

  return (
    <section className="lp-section-ink lp-section-pad">
      <div className="lp-section-inner">
        <div ref={ref} className="lp-score-grid">
          <div>
            <span className="lp-eyebrow">Pre-publish intelligence</span>
            <h2 className="lp-section-heading">Know which clip to post first</h2>
            <p className="lp-score-sub">
              AI does not just cut your video — it rates every clip it produces, and writes the
              copy to go with it. You publish the best one, not the first one.
            </p>
            <div className="lp-score-points">
              {SCORE_POINTS.map(pt => (
                <div key={pt.title} className="lp-score-point">
                  <span className="lp-score-point-arrow">→</span>
                  <div>
                    <p className="lp-score-point-title">{pt.title}</p>
                    <p className="lp-score-point-desc">{pt.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/login" className="lp-btn-ghost-primary">Score My First Video →</Link>
          </div>

          <div className="lp-score-col">
            <div className="lp-meter-wrap">
              <svg className="lp-meter-svg" viewBox="0 0 200 200" aria-label={`Confidence Score: ${SAMPLE_CLIP.score} out of 100`}>
                <defs>
                  <linearGradient id="meterGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#1BC8A6" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </linearGradient>
                </defs>
                <circle className="lp-meter-track" cx="100" cy="100" r="80"
                  strokeDasharray="377 126"
                  transform="rotate(135 100 100)"
                />
                <circle className="lp-meter-fill" cx="100" cy="100" r="80"
                  style={{ strokeDashoffset: visible ? arcOffset : ARC_LENGTH }}
                />
                <text className="lp-meter-score" x="100" y="96">{SAMPLE_CLIP.score}</text>
                <text className="lp-meter-label" x="100" y="118">Confidence</text>
              </svg>
            </div>

            {/* Mirrors the clip detail panel in the app */}
            <div className="lp-clip-card">
              <div className="lp-clip-card-head">
                <span className="lp-clip-card-eyebrow">Clip details</span>
                <span className="lp-clip-card-score">{SAMPLE_CLIP.score}/100</span>
              </div>
              <p className="lp-clip-card-title">{SAMPLE_CLIP.title}</p>
              <p className="lp-clip-card-desc">{SAMPLE_CLIP.description}</p>
              <div className="lp-clip-card-tags">
                {SAMPLE_CLIP.tags.map(t => <span key={t} className="lp-clip-tag">{t}</span>)}
              </div>
              <div className="lp-clip-card-meta">
                <span><b>{SAMPLE_CLIP.duration}</b> duration</span>
                <span><b>{SAMPLE_CLIP.sourceRange}</b> in source</span>
              </div>
            </div>
          </div>
        </div>

        <ZoomShot shot={LENS.clipDetails} className="lp-score-strip" label="The clip detail panel, straight from the app" />
      </div>
    </section>
  );
}

// ── Pricing ──
type PricingTab = 'payg' | 'subscription' | 'b2b';

const RECOMMENDED_PLAN_KEYWORDS = ['growth', 'pro'];

function LandingPricing() {
  const [tab, setTab] = useState<PricingTab>('subscription');
  const [catalog, setCatalog] = useState<PricingCatalog | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/pricing/catalog`, { credentials: 'include' })
      .then(r => r.json())
      .then((data: PricingCatalog) => { setCatalog(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const quotaKeys = ['slideshow', 'shorts', 'subtitle', 'youtube_posts'];
  const paygRows = catalog?.payg.filter(p => quotaKeys.includes(p.featureKey)) ?? [];
  const plans = (catalog?.plans ?? []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <section className="lp-section-surf lp-section-pad" id="pricing">
      <div className="lp-section-inner">
        <div className="lp-pricing-header">
          <span className="lp-eyebrow">Simple pricing</span>
          <h2 className="lp-section-heading" style={{ maxWidth: 640, margin: '0 auto' }}>
            Pay for what you use, or pick a plan that scales with you
          </h2>
          <div className="lp-pill-tabs">
            <button className={`lp-pill-tab${tab === 'subscription' ? ' active' : ''}`} onClick={() => setTab('subscription')}>Subscription</button>
            <button className={`lp-pill-tab${tab === 'b2b' ? ' active' : ''}`}          onClick={() => setTab('b2b')}>B2B Offer</button>
          </div>
        </div>

        <div className="lp-pricing-content">
          {tab === 'payg' && (
            loading
              ? <div className="lp-payg-loading">Loading pricing…</div>
              : <PaygTable rows={paygRows} />
          )}
          {tab === 'subscription' && (
            loading
              ? <div className="lp-payg-loading">Loading pricing…</div>
              : <PlansGrid plans={plans} />
          )}
          {tab === 'b2b' && <B2BCard />}
        </div>
      </div>
    </section>
  );
}

function PaygTable({ rows }: { rows: PaygItem[] }) {
  return (
    <div className="lp-payg-table">
      <div className="lp-payg-header-row">
        <span className="lp-payg-header-cell">Feature</span>
        <span className="lp-payg-header-cell">Unit</span>
        <span className="lp-payg-header-cell">Price</span>
        <span className="lp-payg-header-cell">Monthly cap</span>
      </div>
      {rows.map(row => (
        <div key={row.featureKey} className="lp-payg-row">
          <span className="lp-payg-cell name">{row.label}</span>
          <span className="lp-payg-cell">per {row.unit}</span>
          <span className="lp-payg-cell price">{row.currency === 'INR' ? '₹' : '$'}{row.price.toFixed(2)}</span>
          <span className="lp-payg-cell">{row.maxPerMonth != null ? row.maxPerMonth.toLocaleString() : 'Unlimited'}</span>
        </div>
      ))}
      <div className="lp-payg-footer">Billed monthly based on usage. No commitment.</div>
    </div>
  );
}

function PlansGrid({ plans }: { plans: Plan[] }) {
  return (
    <div className="lp-plans">
      {plans.map(plan => {
        const isRec = RECOMMENDED_PLAN_KEYWORDS.some(k => plan.name.toLowerCase().includes(k));
        const quotaLimits = plan.limits.filter(l => ['slideshow','shorts','subtitle','youtube_posts'].includes(l.featureKey));
        return (
          <div key={plan.id} className={`lp-plan-card${isRec ? ' recommended' : ''}`}>
            {isRec && <div className="lp-plan-badge">Recommended</div>}
            <p className="lp-plan-name">{plan.name}</p>
            <div className="lp-plan-price">
              <span className="lp-plan-price-num">{plan.currency === 'INR' ? '₹' : '$'}{plan.price % 1 === 0 ? plan.price : plan.price.toFixed(2)}</span>
              <span className="lp-plan-price-period">/month</span>
            </div>
            <ul className="lp-plan-limits">
              {quotaLimits.map(l => (
                <li key={l.featureKey} className="lp-plan-limit">
                  <span className="lp-plan-limit-check">✓</span>
                  <span>{l.maxPerMonth != null ? `${l.maxPerMonth.toLocaleString()} ${l.label}` : `Unlimited ${l.label}`}</span>
                </li>
              ))}
            </ul>
            <p className="lp-plan-overage">Additional usage billed at PAYG rates.</p>
            <Link
              to="/login"
              className={`lp-plan-cta${isRec ? ' filled' : ' ghost'}`}
              onClick={() => setPendingSelection({ kind: 'plan', planId: plan.id })}
            >
              {isRec ? 'Get Started →' : 'Choose Plan'}
            </Link>
          </div>
        );
      })}
    </div>
  );
}

function B2BCard() {
  return (
    <div className="lp-b2b-card">
      <div className="lp-b2b-badge">B2B Launch Offer</div>
      <div className="lp-b2b-inner">
        <div>
          <h3 className="lp-b2b-title">Built for teams. Priced for scale.</h3>
          <ul className="lp-b2b-list">
            {[
              '20% off on annual contracts.',
              'Dedicated onboarding session (up to 3 hours).',
              'Priority support SLA.',
              'Custom team seats bundle.',
              'Dedicated success manager for qualifying plans.',
            ].map(item => (
              <li key={item} className="lp-b2b-item">
                <span className="lp-b2b-arrow">→</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lp-b2b-cta-col">
          <a href="mailto:creator@viralai.tech" className="lp-b2b-cta">Talk to Sales →</a>
          <span className="lp-b2b-contact">Or email creator@viralai.tech</span>
        </div>
      </div>
    </div>
  );
}

// ── FAQ ──
function LandingFaq() {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <section className="lp-section-ink lp-section-pad-sm">
      <div className="lp-section-inner">
        <div className="lp-faq-header">
          <span className="lp-eyebrow">FAQ</span>
          <h2 className="lp-section-heading">Common questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((faq, i) => (
            <div key={i} className={`lp-faq-item${open === i ? ' open' : ''}`}>
              <button className="lp-faq-question" onClick={() => setOpen(open === i ? null : i)}>
                {faq.q}
                <span className="lp-faq-icon">+</span>
              </button>
              <div className="lp-faq-answer">{faq.a}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Footer CTA ──
function LandingFooterCta() {
  return (
    <>
      <div className="lp-cta-band">
        <h2 className="lp-cta-headline">Start creating content from Day 1.</h2>
        <p className="lp-cta-sub">Upload once. Generate, optimize, and publish in minutes.</p>
        <Link to="/login" className="lp-cta-btn">Start Creating Free →</Link>
        <p className="lp-cta-micronote">No credit card required.</p>
      </div>
      <footer className="lp-footer-row">
        <span className="lp-footer-copy">© 2026 ViralAI</span>
        <div className="lp-footer-links">
          <Link to="/privacy" className="lp-footer-link">Privacy</Link>
          <Link to="/terms"   className="lp-footer-link">Terms</Link>
          <a href="mailto:support@vimapi.com" className="lp-footer-link">Contact</a>
        </div>
      </footer>
    </>
  );
}

// ── Page ──
const LandingPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  if (isLoading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="lp">
      <Helmet>
        <title>ViralAI | AI Shorts, Kinetic Captions & Story Builder</title>
        <meta name="description" content="Become a content creator from Day 1. Turn long videos into scored, titled AI shorts, style them with nine kinetic caption presets, build slide-by-slide stories, and publish to multiple channels from one workspace." />
        <link rel="canonical" href="https://www.vimapi.com/" />
        <meta property="og:title" content="ViralAI | AI Shorts, Kinetic Captions & Story Builder" />
        <meta property="og:description" content="Upload once. AI cuts, titles and scores your shorts — then edit segments and captions before you publish." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:site_name" content="ViralAI" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="ViralAI — turn long videos into shorts. The shorts workspace showing a rendered clip with its confidence score, title and tags." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ViralAI | AI Shorts, Kinetic Captions & Story Builder" />
        <meta name="twitter:description" content="Upload once. AI cuts, titles and scores your shorts — then edit segments and captions before you publish." />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(SOFTWARE_JSONLD)}</script>
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>

      <LandingNav menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <LandingHero />
      <LandingFeatures />
      <LandingHowItWorks />
      <LandingProductPreview />
      <LandingCaptionStyles />
      <LandingLanguages />
      <LandingConfidenceScore />
      <LandingPricing />
      <LandingFaq />
      <LandingFooterCta />
    </div>
  );
};

export default LandingPage;
